// src/features/devices/components/OperatorCodeButton.tsx
//
// «Código de operador» outside the device list (product decision P06, refined
// 15/10): reception staff and hotel administrators open a key dispenser's
// operator menu with the code of the day. Offered only to the roles that may
// generate it and only when the hotels in view have a dispenser; one
// dispenser opens the code at once, several are listed first.
import React, { useState } from 'react';
import { Button, IconButton, ListItemText, Menu, MenuItem, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { VpnKeyRounded as KeyIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider, usePermissions, useTranslate } from 'react-admin';
import { canGenerateOperatorCode } from '../hooks/useDevicePermissions';
import OtpDialog from './OtpDialog';
import type { OperatorCodeDispenser, UserRole } from '@/types/models';

interface OperatorCodeButtonProps {
  /** Hotels in view; none = the user's own hotels (the API decides). */
  hotelIds?: string[];
}

export const OperatorCodeButton: React.FC<OperatorCodeButtonProps> = ({ hotelIds }) => {
  const translate = useTranslate();
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const allowed = canGenerateOperatorCode(permissions as UserRole);
  const key = (hotelIds ?? []).slice().sort().join(',');

  const { data: dispensers = [] } = useQuery<OperatorCodeDispenser[]>({
    queryKey: ['insight-v1', 'devices', 'operator-code-dispensers', key],
    queryFn: async () => (await (dataProvider as any).getOperatorCodeDispensers(hotelIds)).data ?? [],
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState<OperatorCodeDispenser | null>(null);

  if (!allowed || dispensers.length === 0) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (dispensers.length === 1) setOpen(dispensers[0]);
    else setAnchor(event.currentTarget);
  };

  return (
    <>
      {isPhone ? (
        <Tooltip title={translate('resources.arrivals.operatorCode')}>
          <IconButton
            onClick={handleClick}
            aria-label={translate('resources.arrivals.operatorCode')}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper', width: 40, height: 40 }}
          >
            <KeyIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<KeyIcon sx={{ fontSize: 16 }} />}
          onClick={handleClick}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {translate('resources.arrivals.operatorCode')}
        </Button>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-label': translate('resources.arrivals.operatorCodeChoose') }}
      >
        {dispensers.map((d) => (
          <MenuItem
            key={d.id}
            onClick={() => {
              setAnchor(null);
              setOpen(d);
            }}
          >
            <ListItemText primary={d.name || d.installationCode} secondary={d.name ? d.installationCode : undefined} />
          </MenuItem>
        ))}
      </Menu>
      {open && <OtpDialog open onClose={() => setOpen(null)} record={open} />}
    </>
  );
};

export default OperatorCodeButton;
